import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PRIORITIES, PRIORITY_LABEL } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2 } from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";

export default function SLAAdminPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", priority: "P3", first_response_minutes: 60, resolution_minutes: 24 * 60, active: true });
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/sla-policies").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const hasPriorityConflict = (priority, excludeId) =>
    items.some((item) => item.priority === priority && item.id !== excludeId);

  const submit = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Name is required");
    if (hasPriorityConflict(form.priority)) {
      return toast.error(`An SLA policy already exists for ${PRIORITY_LABEL[form.priority]}.`);
    }
    try {
      await api.post("/sla-policies", { ...form, name });
      toast.success("SLA policy created");
      setForm({ name: "", priority: "P3", first_response_minutes: 60, resolution_minutes: 24 * 60, active: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (p) => { await api.patch(`/sla-policies/${p.id}`, { active: !p.active }); load(); };

  const startEdit = (p) => setEditing({ ...p });
  const saveEdit = async () => {
    const name = (editing.name || "").trim();
    if (!name) return toast.error("Name is required");
    const original = items.find((item) => item.id === editing.id);
    if (
      original &&
      editing.priority !== original.priority &&
      hasPriorityConflict(editing.priority, editing.id)
    ) {
      return toast.error(`An SLA policy already exists for ${PRIORITY_LABEL[editing.priority]}.`);
    }
    try {
      await api.patch(`/sla-policies/${editing.id}`, {
        name,
        priority: editing.priority,
        first_response_minutes: Number(editing.first_response_minutes),
        resolution_minutes: Number(editing.resolution_minutes),
        active: editing.active,
      });
      toast.success("Policy updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doDelete = async (id) => {
    try {
      await api.delete(`/sla-policies/${id}`);
      setConfirmDelete(null);
      toast.success("Deleted successfully");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-sla-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">SLA policies</h1>
        <p className="text-sm text-muted-foreground">Configure response and resolution targets per priority. Applied automatically once a ticket is approved for support.</p>
      </div>

      <EscalationRules />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">First response</th>
                <th className="text-left px-4 py-3">Resolution</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-row data-testid={`sla-row-${p.priority}-${p.id.slice(0, 8)}`} className="border-b border-border/70">
                  <td className="px-4 py-3">{p.name}</td>
                  <td className="px-4 py-3 font-mono">{p.priority}</td>
                  <td className="px-4 py-3">{p.first_response_minutes} min</td>
                  <td className="px-4 py-3">{p.resolution_minutes} min</td>
                  <td className="px-4 py-3"><Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`sla-toggle-${p.id}`} /></td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => startEdit(p)} data-testid={`sla-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>{" "}
                    <Button variant="outline" size="sm" onClick={() => setConfirmDelete(p)} data-testid={`sla-delete-${p.id}`} className="text-rose-700 border-rose-500/30 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3 h-fit">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">New policy</div>
          <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="sla-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="sla-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>First response (min)</Label><Input data-testid="sla-first" type="number" value={form.first_response_minutes} onChange={(e) => setForm({ ...form, first_response_minutes: Number(e.target.value) })} /></div>
          <div><Label>Resolution (min)</Label><Input data-testid="sla-resolution" type="number" value={form.resolution_minutes} onChange={(e) => setForm({ ...form, resolution_minutes: Number(e.target.value) })} /></div>
          <Button data-testid="sla-create" onClick={submit} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">Create policy</Button>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-sla-dialog">
          <DialogHeader><DialogTitle>Edit SLA policy</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="edit-sla-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div>
                <Label>Priority</Label>
                <Select value={editing.priority} onValueChange={(v) => setEditing({ ...editing, priority: v })}>
                  <SelectTrigger data-testid="edit-sla-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>First response (min)</Label><Input data-testid="edit-sla-first" type="number" value={editing.first_response_minutes} onChange={(e) => setEditing({ ...editing, first_response_minutes: e.target.value })} /></div>
              <div><Label>Resolution (min)</Label><Input data-testid="edit-sla-resolution" type="number" value={editing.resolution_minutes} onChange={(e) => setEditing({ ...editing, resolution_minutes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="edit-sla-save" onClick={saveEdit} className="bg-primary text-primary-foreground hover:bg-primary/90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={confirmDelete ? `Delete "${confirmDelete.name}"?` : "Delete?"}
        description="Soft delete — the policy will be hidden. Policies attached to open tickets cannot be deleted."
        confirmLabel="Delete"
        destructive
        onConfirm={() => confirmDelete && doDelete(confirmDelete.id)}
        testid="confirm-delete-sla"
      />
    </div>
  );
}


// Reminder / escalation timings used by the backend SLA monitor.
function EscalationRules() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/sla-escalation").then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!cfg) return null;

  const field = (key, label, hint) => (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        min={1}
        max={720}
        value={cfg[key]}
        onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
        data-testid={`sla-esc-${key}`}
      />
      <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/sla-escalation", {
        enabled: cfg.enabled,
        user_reminder_hours: Number(cfg.user_reminder_hours),
        manager_escalation_hours: Number(cfg.manager_escalation_hours),
        manager_reminder_hours: Number(cfg.manager_reminder_hours),
      });
      setCfg(data);
      toast.success("Escalation rules saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="sla-escalation-rules">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">SLA reminders &amp; escalation</h2>
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
            Measured on the SLA clock from submission (paused while waiting on the client, vendor or access).
            Stops automatically once the ticket is completed or resolved. Every message is kept in the ticket history.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium">
          <Switch
            checked={Boolean(cfg.enabled)}
            onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            data-testid="sla-esc-enabled"
          />
          {cfg.enabled ? "Enabled" : "Disabled"}
        </label>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        {field("user_reminder_hours", "Remind engineer every (hours)", "Sent to the assigned engineer until the ticket is completed.")}
        {field("manager_escalation_hours", "Escalate to manager after (hours)", "Uses the engineer's manager, else Support Admins.")}
        {field("manager_reminder_hours", "Remind manager every (hours)", "Repeats after escalation until the ticket is completed.")}
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="sla-esc-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
          {saving ? "Saving…" : "Save rules"}
        </Button>
      </div>
    </div>
  );
}
